import { DeleteOutlined, FileOutlined, InboxOutlined, SwapOutlined } from '@ant-design/icons';
import { Button, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { useTranslation } from 'react-i18next';

interface CompactActionFilePickerProps {
  accept: string;
  disabled?: boolean;
  file: File | null;
  hint: string;
  label: string;
  onChange: (file: File | null) => void;
  onInvalid: () => void;
}

const formatFileSize = (size: number): string => {
  if (size < 1024 * 1024) return `${Math.max(1, Math.ceil(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/** Keeps the upload control compact after a file has been selected. */
const CompactActionFilePicker = ({ accept, disabled = false, file, hint, label, onChange, onInvalid }: CompactActionFilePickerProps) => {
  const { t } = useTranslation();
  const uploadProps: UploadProps = {
    accept,
    beforeUpload: (nextFile) => {
      if (!nextFile.name.toLowerCase().endsWith(accept.toLowerCase())) {
        onChange(null);
        onInvalid();
        return Upload.LIST_IGNORE;
      }
      onChange(nextFile as File);
      return false;
    },
    fileList: [],
    maxCount: 1,
    showUploadList: false,
  };

  if (file) {
    return (
      <div className="dms-action-file-picker__selected">
        <FileOutlined aria-hidden="true" />
        <div className="dms-action-file-picker__metadata">
          <strong title={file.name}>{file.name}</strong>
          <span>{formatFileSize(file.size)}</span>
        </div>
        <Upload {...uploadProps} disabled={disabled}>
          <Button
            aria-label={t('actionsModal.replaceFile')}
            disabled={disabled}
            icon={<SwapOutlined />}
            size="small"
          >
            {t('actionsModal.replaceFile')}
          </Button>
        </Upload>
        <Button aria-label={t('actionsModal.removeFile')} disabled={disabled} icon={<DeleteOutlined />} onClick={() => onChange(null)} size="small" type="text" />
      </div>
    );
  }

  return (
    <Upload.Dragger {...uploadProps} className="dms-action-file-picker__dropzone" disabled={disabled} aria-label={label}>
      <p className="ant-upload-drag-icon"><InboxOutlined /></p>
      <p className="ant-upload-text">{label}</p>
      <p className="ant-upload-hint">{hint}</p>
    </Upload.Dragger>
  );
};

export default CompactActionFilePicker;
